import {
    Box, Button, styled, CircularProgress, Tooltip,
} from '@mui/material';
import { PlayArrow, RestartAlt } from '@mui/icons-material';

const ActionsBar = styled(Box)(({ theme }) => ({
    position: 'sticky',
    bottom: 0,
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
    padding: theme.spacing(2, 3),
}));

type LoadResetButtonProps = {
    uri: string;
    dpcId?: string;
    designConceptId?: string;
    brandId?: string;
    culture?: string;
    isLoading?: boolean;
    onLoad?: (uri: string) => void;
    onReset?: () => void;
    productKey?: string;
    productVersion?: number;
    hasAnyInput?: boolean;
};

const LOAD_DISABLED_HINT = 'Provide one of Document Source, DPC ID, Design Concept ID, or Brand Kit ID (plus a Product Key, Version, and Culture) to enable Load.';

export const LoadResetButtons = ({
    uri,
    dpcId = '',
    designConceptId = '',
    brandId = '',
    culture = '',
    isLoading = false,
    onLoad,
    onReset,
    productKey,
    productVersion,
    hasAnyInput = false,
}: LoadResetButtonProps): JSX.Element => {
    const hasSource = !!uri.trim() || !!dpcId.trim() || !!designConceptId.trim() || !!brandId.trim();
    const canLoad = hasSource && !!productKey?.trim() && !!productVersion && !!culture && !isLoading;
    const canReset = hasAnyInput && !isLoading;

    const handleLoad = (): void => {
        if (!canLoad) {
            return;
        }
        onLoad?.(uri.trim());
    };

    const handleReset = (): void => {
        onReset?.();
    };

    return (
        <ActionsBar>
            <Button
                color="secondary"
                disabled={!canReset}
                startIcon={<RestartAlt />}
                variant="outlined"
                onClick={handleReset}
            >
                Reset
            </Button>
            <Tooltip title={!canLoad && !isLoading ? LOAD_DISABLED_HINT : ''}>
                <span>
                    <Button
                        color="primary"
                        disabled={!canLoad}
                        startIcon={isLoading ? <CircularProgress size={20} /> : <PlayArrow />}
                        variant="contained"
                        onClick={handleLoad}
                    >
                        {isLoading ? 'Loading...' : 'Load'}
                    </Button>
                </span>
            </Tooltip>
        </ActionsBar>
    );
};
